export const MAX_EDGE = 1600;
export const JPEG_QUALITY = 0.85;
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

/**
 * Scale the longest edge down to `maxEdge`, preserving aspect ratio. Pulled
 * out as a pure function so it can be unit-tested in node — compressImage
 * itself needs a canvas and can only be verified by hand in a browser.
 */
export function planCompression(
  width: number,
  height: number,
  maxEdge = MAX_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Phone screenshots routinely run 5MB+; rejecting them outright just makes
 * players give up on submitting feedback. Downscale in the browser first —
 * most screenshots land well under 300KB after this — and let the caller
 * reject anything still over MAX_IMAGE_BYTES once compressed.
 */
export async function compressImage(file: File): Promise<{ blob: Blob; type: string }> {
  const bitmap = await createImageBitmap(file);
  const plan = planCompression(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = plan.width;
  canvas.height = plan.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('CANVAS_UNAVAILABLE');
  ctx.drawImage(bitmap, 0, 0, plan.width, plan.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) throw new Error('COMPRESS_FAILED');
  return { blob, type: 'image/jpeg' };
}
